import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Badge, 
  Spinner, 
  Alert, 
  Container, 
  Row, 
  Col,
  Card,
  Form,
  Dropdown,
  ButtonGroup
} from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { 
  getAppraisals, 
  getAppraisalStats,
  deleteAppraisal
} from '../services/appraisalService';
import { getEmployees } from '../../../services/employeeService';
import { FaEye, FaEdit, FaTrash, FaFilter, FaSearch, FaEllipsisV } from 'react-icons/fa';

const AppraisalList = () => {
  const [appraisals, setAppraisals] = useState([]);
  const [employees, setEmployees] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    employee: 'all',
    search: ''
  });
  const navigate = useNavigate();

  const fetchAppraisalData = async () => {
    try {
      setLoading(true);
      
      // Fetch appraisals and employees in parallel
      const [appraisalsRes, statsRes, employeesRes] = await Promise.all([
        getAppraisals(),
        getAppraisalStats(),
        getEmployees()
      ]);
      console.log('Appraisals response:', appraisalsRes.data);
      console.log('Stats response:', statsRes);
      console.log('Employees response:', employeesRes.data);
      
      setAppraisals(appraisalsRes.data || []);
      setStats(statsRes || {});
      
      // Create employee map for dropdown
      const employeeMap = {};
      (employeesRes?.data || []).forEach(emp => {
        employeeMap[emp.id] = emp.name || 'Unknown Employee';
      });
      setEmployees(employeeMap);
      
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load appraisals. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppraisalData();
  }, []);
  
  // Expose the refetch function to child components
  const refetchAppraisals = async () => {
    try {
      const [appraisalsRes, statsRes] = await Promise.all([
        getAppraisals(),
        getAppraisalStats()
      ]);
      setAppraisals(appraisalsRes.data || []);
      setStats(statsRes?.data || {});
    } catch (err) {
      console.error('Error refetching data:', err);
      setError('Failed to refresh data. Please try again.');
    }
  };
  
  // Refetch function is available for child components if needed
  
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this appraisal? This action cannot be undone.')) {
      try {
        await deleteAppraisal(id);
        // Refresh both appraisals and stats
        const [appraisalsRes, statsRes] = await Promise.all([
          getAppraisals(),
          getAppraisalStats()
        ]);
        setAppraisals(appraisalsRes.data || []);
        setStats(statsRes?.data || {});
      } catch (err) {
        console.error('Error deleting appraisal:', err);
        setError('Failed to delete appraisal. Please try again.');
      }
    }
  };
  
  const filteredAppraisals = appraisals.filter(appraisal => {
    // Filter by status
    if (filters.status !== 'all' && appraisal.status !== filters.status) {
      return false;
    }
    
    // Filter by employee
    if (filters.employee !== 'all' && String(appraisal.employee_id) !== filters.employee) {
      return false;
    }
    
    // Filter by search term
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const employeeName = employees[appraisal.employee_id]?.toLowerCase() || '';
      if (!employeeName.includes(searchLower)) {
        return false;
      }
    }
    
    return true;
  });

  if (loading) {
    return (
      <Container className="my-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-2">Loading appraisals...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="my-5">
        <Alert variant="danger">
          <Alert.Heading>Error Loading Appraisals</Alert.Heading>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={() => window.location.reload()}>
            Retry
          </Button>
        </Alert>
      </Container>
    );
  }

  const getStatusVariant = (status) => {
    if (!status) return 'secondary';
    
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case 'completed':
        return 'success';
      case 'in_progress':
      case 'in progress':
        return 'primary';
      case 'draft':
        return 'secondary';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'danger';
      default:
        return 'info';
    }
  };
  
  const formatDate = (dateString) => {
    return dateString ? moment(dateString).format('MMM D, YYYY') : 'N/A';
  };
  
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <Container fluid className="py-4">
      {/* Summary Card */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body className="p-4">
          <Row className="align-items-center">
            <Col md={8}>
              <div className="d-flex align-items-center">
                <div className="icon-shape icon-lg bg-primary-soft text-primary rounded-3 me-3">
                  <i className="bi bi-clipboard-data fs-4"></i>
                </div>
                <div>
                  <h2 className="mb-1">Performance Appraisals</h2>
                  <p className="text-muted mb-0">Manage and review employee performance evaluations</p>
                </div>
              </div>
            </Col>
            <Col md={4} className="text-md-end mt-3 mt-md-0">
              <Button 
                as={Link} 
                to="/app/appraisals/new" 
                variant="primary" 
                className="px-4"
              >
                <i className="bi bi-plus-lg me-2"></i>New Appraisal
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      
      {/* Stats Cards */}
      {stats && (
        <Row className="mb-4">
          <Col md={3} sm={6} className="mb-3 mb-md-0">
            <Card className="border-0 bg-soft-primary">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-uppercase text-muted mb-0">Total</h6>
                    <h3 className="mb-0">{stats.total || 0}</h3>
                  </div>
                  <div className="icon-shape icon-lg bg-primary-soft text-primary rounded-3">
                    <i className="bi bi-clipboard-data fs-4"></i>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3} sm={6} className="mb-3 mb-md-0">
            <Card className="border-0 bg-soft-success">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-uppercase text-muted mb-0">Completed</h6>
                    <h3 className="mb-0">{stats.completed || 0}</h3>
                  </div>
                  <div className="icon-shape icon-lg bg-success-soft text-success rounded-3">
                    <i className="bi bi-check-circle fs-4"></i>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3} sm={6} className="mb-3 mb-md-0">
            <Card className="border-0 bg-soft-info">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-uppercase text-muted mb-0">In Progress</h6>
                    <h3 className="mb-0">{stats.in_progress || 0}</h3>
                  </div>
                  <div className="icon-shape icon-lg bg-info-soft text-info rounded-3">
                    <i className="bi bi-arrow-repeat fs-4"></i>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3} sm={6}>
            <Card className="border-0 bg-soft-warning">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-uppercase text-muted mb-0">Avg. Rating</h6>
                    <h3 className="mb-0">
                      {stats.average_rating ? stats.average_rating.toFixed(1) : 'N/A'}
                    </h3>
                  </div>
                  <div className="icon-shape icon-lg bg-warning-soft text-warning rounded-3">
                    <i className="bi bi-star-fill fs-4"></i>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
      
      {/* Filters */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={4} className="mb-3 mb-md-0">
              <Form.Label>Employee</Form.Label>
              <Form.Select 
                name="employee"
                value={filters.employee}
                onChange={handleFilterChange}
              >
                <option value="all">All Employees</option>
                {Object.entries(employees).map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3} className="mb-3 mb-md-0">
              <Form.Label>Status</Form.Label>
              <Form.Select 
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
              >
                <option value="all">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </Form.Select>
            </Col>
            <Col md={5}>
              <Form.Label>Search</Form.Label>
              <div className="input-group">
                <span className="input-group-text"><FaSearch /></span>
                <Form.Control
                  type="text"
                  name="search"
                  placeholder="Search by employee name..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                />
                <Button variant="outline-secondary">
                  <FaFilter className="me-1" /> Filter
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      
      {/* Appraisals Table */}
      <Card>
        <div className="table-responsive">
          <Table hover className="mb-0">
            <thead className="table-light">
              <tr>
                <th>Employee</th>
                <th>Review Period</th>
                <th>Rating</th>
                <th>Status</th>
                <th>Next Review Date</th>
                <th>Last Updated</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAppraisals.length > 0 ? (
                filteredAppraisals.map((appraisal) => {
                  const employeeName = employees[appraisal.employee_id] || 'Unknown Employee';
                  const rating = appraisal.overall_rating || 0;
                  
                  return (
                    <tr key={appraisal.id}>
                      <td>
                        <div className="d-flex align-items-center">
                          <div className="flex-shrink-0 me-2">
                            <div className="avatar-sm bg-soft-primary rounded-circle d-flex align-items-center justify-content-center">
                              <span className="text-primary fw-medium">
                                {employeeName.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          </div>
                          <div>
                            <h6 className="mb-0">{employeeName}</h6>
                            <small className="text-muted">ID: {appraisal.employee_id}</small>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div>
                          <span className="d-block">{formatDate(appraisal.review_period_start)}</span>
                          <small className="text-muted">to {formatDate(appraisal.review_period_end)}</small>
                        </div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <div className="progress flex-grow-1 me-2" style={{ height: '6px' }}>
                            <div 
                              className="progress-bar bg-success" 
                              role="progressbar" 
                              style={{ width: `${(rating / 5) * 100}%` }}
                              aria-valuenow={rating}
                              aria-valuemin="0"
                              aria-valuemax="5"
                            ></div>
                          </div>
                          <span className="fw-medium">{rating.toFixed(1)}/5</span>
                        </div>
                      </td>
                      <td>
                        <Badge bg={getStatusVariant(appraisal.status)} className="text-capitalize">
                          {appraisal.status?.replace('_', ' ')}
                        </Badge>
                      </td>
                      <td>
                        <div>
                          <div>{formatDate(appraisal.updated_at)}</div>
                          <small className="text-muted">
                            {appraisal.completed_at ? `Completed: ${formatDate(appraisal.completed_at)}` : 'In Progress'}
                          </small>
                        </div>
                      </td>
                      <td>
                        {appraisal.status === 'completed' && appraisal.next_review_date ? (
                          <div className="fw-medium">
                            {formatDate(appraisal.next_review_date)}
                          </div>
                        ) : (
                          <span className="text-muted">N/A</span>
                        )}
                      </td>
                      <td className="text-end" style={{ position: 'relative' }}>
                        <Dropdown className="action-dropdown">
                          <Dropdown.Toggle 
                            variant="link" 
                            className="text-muted p-0"
                            style={{ zIndex: 1000 }}
                          >
                            <FaEllipsisV />
                          </Dropdown.Toggle>
                          <Dropdown.Menu 
                            align="end"
                            style={{
                              position: 'absolute',
                              zIndex: 1050,
                              right: 0,
                              left: 'auto',
                              marginTop: '0.5rem'
                            }}
                          >
                            <Dropdown.Item onClick={() => navigate(`/app/appraisals/view/${appraisal.id}`)}>
                              <FaEye className="me-2" /> View
                            </Dropdown.Item>
                            <Dropdown.Item 
                              onClick={() => navigate(`/app/appraisals/edit/${appraisal.id}`)}
                              disabled={appraisal.status === 'completed'}
                            >
                              <FaEdit className="me-2" /> Edit
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item 
                              className="text-danger" 
                              onClick={() => handleDelete(appraisal.id)}
                              disabled={appraisal.status === 'completed'}
                            >
                              <FaTrash className="me-2" /> Delete
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6" className="text-center py-4">
                    <div className="text-muted">
                      <i className="bi bi-inbox fs-1 d-block mb-2"></i>
                      No appraisals found
                    </div>
                    {appraisals.length === 0 && (
                      <Button as={Link} to="/app/appraisals/new" variant="outline-primary" size="sm" className="mt-2">
                        Create Your First Appraisal
                      </Button>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </Card>
    </Container>
  )
};

export default AppraisalList;
